// Per-USER cube navigation direction prefs (Jun 2026, Randy). Each value is a
// small flip: 1 = normal, -1 = invert. For wheel horizontal, 0 = off (a plain
// mouse has no horizontal axis). Applies to all of the user's cubes everywhere.
import { api } from "@/lib/api";

export const DEFAULT_NAV_PREFS = {
  // ⚠️ KEEP IN SYNC with backend _CUBE_NAV_DEFAULTS (server.py) — the API fills
  // missing fields from there, so a frontend-only change is silently overridden.
  desktop: { arrows: { v: 1, h: 1 }, wheel: { v: -1, h: 0 } },
  mobile: { arrows: { v: 1, h: 1 }, swipe: { v: -1, h: 1 } },
};

const clone = (o) => JSON.parse(JSON.stringify(o));

export function normalizeNavPrefs(raw) {
  const out = clone(DEFAULT_NAV_PREFS);
  if (!raw || typeof raw !== "object") return out;
  for (const ctx of ["desktop", "mobile"]) {
    const c = raw[ctx];
    if (!c || typeof c !== "object") continue;
    for (const inp of Object.keys(out[ctx])) {
      const src = c[inp];
      if (!src || typeof src !== "object") continue;
      for (const ax of ["v", "h"]) {
        if ([-1, 0, 1].includes(src[ax])) out[ctx][inp][ax] = src[ax];
      }
    }
  }
  return out;
}

export function isCoarsePointer() {
  try {
    return !!(window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
  } catch {
    return false;
  }
}

// Flip a direction per an {v,h} axis pref. h===0 means that axis is OFF (no nav).
// Returns the (possibly flipped) direction, or null when the axis is off.
export function applyFlip(dir, axes) {
  if (!axes) return dir;
  if (dir === "up" || dir === "down") {
    return axes.v === -1 ? (dir === "up" ? "down" : "up") : dir;
  }
  if (dir === "left" || dir === "right") {
    if (axes.h === 0) return null;
    return axes.h === -1 ? (dir === "left" ? "right" : "left") : dir;
  }
  return dir;
}

let _cache = null;
let _loading = null;
const _subs = new Set();

export function getNavPrefsCached() {
  return _cache || clone(DEFAULT_NAV_PREFS);
}

export async function loadNavPrefs(force = false) {
  if (_cache && !force) return _cache;
  if (_loading && !force) return _loading;
  _loading = (async () => {
    try {
      const r = await api.get("/me/cube-nav-prefs");
      _cache = normalizeNavPrefs(r.data?.prefs);
    } catch {
      _cache = clone(DEFAULT_NAV_PREFS);
    }
    _loading = null;
    return _cache;
  })();
  return _loading;
}

export async function saveNavPrefs(prefs) {
  const norm = normalizeNavPrefs(prefs);
  _cache = norm;
  _subs.forEach((fn) => { try { fn(norm); } catch { /* */ } });
  try { await api.patch("/me/cube-nav-prefs", { prefs: norm }); } catch { /* */ }
  return norm;
}

export function subscribeNavPrefs(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}
